import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { loadLayouts, loadPricinglists, addEmailLayout, removeEmailLayout } from '../../redux';
import { EmailLayout } from '../../models';
import { EmailLayoutsField } from '../../components';
import CardIconAction from '../CardIconAction/CardIconAction';
import RedundantOverridesAlert from '../RedundantOverridesAlert';
import { find_redundant_email_overrides } from '../../services/redundantOverrides';
import { addable_email_override_pricings, pricing_display_names } from '../../services/layoutReferenceUtils';
import { EMPTY_OBJECT, Utils } from '../../services';
import { Row, Col, Card, CardBody, CardText, CardTitle, Button, InputGroup, InputGroupAddon } from "reactstrap";
import { PricingsField } from '..';

import './EmailLayoutMappings.css';
import '../CardIconAction/CardIconAction.scss';

const formSelector = formValueSelector('tickettypeForm');

const EmailLayoutMappings = ({ name, id: _id, pricingName: _pricingName }) => {
    const [currentPricingId, setCurrentPricingId] = useState(null);
    const [pricingSelectKey, setPricingSelectKey] = useState(0);

    const { layouts }               = useSelector(state => state.layouts);
    const { tickettype, isLoading } = useSelector(state => state.tickettypes);
    const { pricinglists }          = useSelector(state => state.pricinglists);
    const emailsFormValues          = useSelector(state => formSelector(state, 'opaque.emails') || EMPTY_OBJECT);

    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        dispatch(loadLayouts());
        dispatch(loadPricinglists());
    }, [dispatch]);

    const hooks = ['creation', 'activation', 'before_expiration', 'expiration', 'renewal', 'deletion'];

    const sortedLayouts = useMemo(() => {
        return layouts?.filter(l => l.layout_type === 'email')
            .sort((a, b) => Utils.localized_or_fallback(a.name, i18n.language).localeCompare(Utils.localized_or_fallback(b.name, i18n.language)));
    }, [i18n.language, layouts]);

    const pricingsToAdd = useMemo(() => (
        addable_email_override_pricings(tickettype, pricinglists)
    ), [tickettype, pricinglists]);

    const overridePricingIds = useMemo(() => (
        Object.keys(tickettype?.opaque?.emails || {}).filter(k => k !== 'default')
    ), [tickettype]);

    const [persistedOverrideIds, setPersistedOverrideIds] = useState([]);

    useEffect(() => {
        setPersistedOverrideIds(overridePricingIds);
    }, [tickettype?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    const pricingNamesFor = (pricingId) => (
        pricing_display_names(tickettype, pricingId, i18n.language, pricinglists)
    );

    const pricingLabel = (pricingId) => {
        const names = pricingNamesFor(pricingId);
        return names.length ? names.join(', ') : pricingId;
    };

    const pricingOverrideTitle = (pricingId) => {
        const names = pricingNamesFor(pricingId);
        return t('layouts.on_pricing', {
            count: names.length || 1,
            pricing: names.join(', ') || pricingId,
        });
    };

    const redundantItems = useMemo(() => (
        find_redundant_email_overrides(emailsFormValues, persistedOverrideIds)
            .filter(id => overridePricingIds.includes(id))
            .map(id => ({ id, label: pricingLabel(id), icon: 'fa-envelope' }))
    // pricingLabel closes over tickettype/i18n/pricinglists
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [emailsFormValues, overridePricingIds, persistedOverrideIds, tickettype, pricinglists, i18n.language]);

    const clearPricingToAdd = () => {
        setCurrentPricingId(null);
        setPricingSelectKey(key => key + 1);
    };

    const addEmail = () => {
        if (!currentPricingId)
            return;

        dispatch(addEmailLayout(currentPricingId));
        clearPricingToAdd();
    };

    const onRemoveLayout = id => {
        dispatch(removeEmailLayout(id));
        clearPricingToAdd();
    };

    return (
        <div className="EmailLayoutMappings">
            <Card className="CardShell mb-3">
                <CardBody>
                    <CardTitle>
                        { overridePricingIds.length > 0 ? t('layouts.on_other_pricings') : t('layouts.on_any_pricing')}
                    </CardTitle>
                    <Row>
                        {hooks?.map(hook => (
                            <Col key={hook} className="PdfLayoutMapping" sm={6}>
                                <Card className="h-auto inner-card">
                                    <CardBody>
                                        <CardTitle className="small">
                                            {t(`layouts.hook_${hook}`)}
                                        </CardTitle>
                                        <CardText tag="div">
                                            <EmailLayoutsField
                                                name={`${name}.${hook}`}
                                                emailLayouts={sortedLayouts}
                                                types={[EmailLayout.LAYOUT_TYPE_TICKET]}
                                                single
                                                placeholder={t('layouts.please_choose_a_layout')}
                                            />
                                        </CardText>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                        <Col className="PdfLayoutMapping" sm={6}>
                            <Card className="h-auto inner-card">
                                <CardBody>
                                    <CardTitle className="small">
                                        {t('layouts.hook_reservation')}
                                    </CardTitle>
                                    <CardText tag="div">
                                        <EmailLayoutsField
                                            name={`${name}.reservation`}
                                            emailLayouts={sortedLayouts}
                                            types={[EmailLayout.LAYOUT_TYPE_BOOKING]}
                                            single
                                            placeholder={t('layouts.hook_reservation_placeholder')}
                                        />
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <div className="EmailLayoutMappings-addOverride mb-3">
                <RedundantOverridesAlert
                    items={redundantItems}
                    onRemove={onRemoveLayout}
                />
                <InputGroup className="w-50">
                    <PricingsField
                        key={pricingSelectKey}
                        pricings={pricingsToAdd || []}
                        value={currentPricingId || ''}
                        onChange={setCurrentPricingId}
                    />
                    <InputGroupAddon addonType="append">
                        <Button color="success" disabled={!currentPricingId || isLoading} onClick={addEmail} >
                            {isLoading ?
                                <span>
                                    &nbsp;<i className="fa fa-spinner fa-spin"></i>
                                </span>
                                :
                                <span>
                                    <i className="fa fa-plus"></i>&nbsp;
                                    {t('tickettypes.add_override')}
                                </span>
                            }
                        </Button>
                    </InputGroupAddon>
                </InputGroup>
            </div>
            {overridePricingIds.map(pricingId =>
                <Row key={pricingId} className="mt-2">
                    <Col>
                        <Card className="CardShell">
                            <CardBody>
                                <CardTitle className="d-flex align-items-center justify-content-start w-100 text-left">
                                    <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start mb-0">
                                        <CardIconAction
                                            id={`delete-email-layout-${pricingId}`}
                                            icon="fa-trash"
                                            danger
                                            title={t('common.card_action_delete_email_override')}
                                            onClick={() => onRemoveLayout(pricingId)}
                                        />
                                    </span>
                                    <span>{pricingOverrideTitle(pricingId)}</span>
                                </CardTitle>
                                <Row>
                                    {hooks?.map(hook => (
                                        <Col key={hook} className="PdfLayoutMapping" sm={6}>
                                            <Card className="h-auto inner-card">
                                                <CardBody>
                                                    <CardTitle className="small">
                                                        {t(`layouts.hook_${hook}`)}
                                                    </CardTitle>
                                                    <CardText tag="div">
                                                        <EmailLayoutsField
                                                            name={`opaque.emails.${pricingId}.${hook}`}
                                                            emailLayouts={sortedLayouts}
                                                            types={[EmailLayout.LAYOUT_TYPE_TICKET]}
                                                            single
                                                            placeholder={t('layouts.please_choose_a_layout')}
                                                        />
                                                    </CardText>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
        </div >
    );
}

export default EmailLayoutMappings;
